import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Activity, Shield, ArrowRight, RefreshCw, Filter } from "lucide-react";
import api from "@/lib/api";

/**
 * AdminAudit — /admin/audit. Reverse-chronological feed combining
 * `admin_extend_audit` (trial extensions by admins) and
 * `analytics_events` (trial_started / trial_ended_early / etc.) into
 * one filterable table. Compliance + regression-catching lens.
 */
const KIND_LABELS = {
  admin_extend: { label: "Admin extend", tone: "warning" },
  trial_started: { label: "Trial started", tone: "info" },
  trial_ended_early: { label: "Trial ended early", tone: "muted" },
};

const DAY_OPTIONS = [1, 7, 30, 90];
const KIND_OPTIONS = ["all", "admin_extend", "trial_started", "trial_ended_early"];

export default function AdminAudit() {
  const [secret, setSecret] = useState("");
  const [days, setDays] = useState(7);
  const [kind, setKind] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (nextDays = days) => {
    if (!secret) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/auth/admin/audit-log?days=${nextDays}&limit=500`, {
        headers: { "X-Admin-Secret": secret },
      });
      setData(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Load failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const filtered = (data?.items || []).filter(r => kind === "all" || r.kind === kind);
  const summary = summarise(data?.items || []);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="text-xs tracking-[0.24em] uppercase text-[var(--text-muted)] mb-2">
          Admin · Audit Log
        </div>
        <h1 className="text-3xl font-heading font-medium text-[var(--text-primary)]">
          Recent admin & trial events
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-xl">
          Trial extensions, trial starts, and early cancellations —
          reverse-chronological. Filter by window and event type.
        </p>
      </div>

      <div className="card-flat p-5 mb-6 flex flex-wrap items-center gap-3">
        <Shield size={16} className="text-[var(--text-secondary)]" />
        <input
          type="password"
          className="field-input flex-1 min-w-[240px]"
          placeholder="Paste ADMIN_SECRET"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          data-testid="admin-audit-secret"
        />
        <div className="flex items-center gap-1 text-xs">
          <span className="text-[var(--text-muted)]">Window:</span>
          {DAY_OPTIONS.map(d => (
            <button
              key={d}
              onClick={() => { setDays(d); if (secret) load(d); }}
              className={`px-2.5 py-1 rounded-md border text-xs transition ${
                days === d
                  ? "border-[var(--accent-primary)] text-[var(--accent-primary)] bg-[var(--accent-primary)]/8"
                  : "border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--text-secondary)]"
              }`}
              data-testid={`admin-audit-window-${d}`}
            >
              {d === 1 ? "24h" : `${d}d`}
            </button>
          ))}
        </div>
        <button
          className="btn-primary"
          onClick={() => load(days)}
          disabled={loading || !secret}
          data-testid="admin-audit-load"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          {loading ? "Loading…" : "Load audit"}
        </button>
      </div>

      {error && (
        <div className="card-flat p-4 mb-6 border-[var(--accent-danger)] text-sm text-[var(--accent-danger)]" data-testid="admin-audit-error">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <SummaryCard label="Total events" value={String(data.count)} icon={<Activity size={14} />} testid="admin-audit-total" />
            <SummaryCard label="Admin extends" value={String(summary.admin_extend)} testid="admin-audit-extends" />
            <SummaryCard label="Trials started" value={String(summary.trial_started)} testid="admin-audit-starts" />
            <SummaryCard label="Trials ended early" value={String(summary.trial_ended_early)} testid="admin-audit-ends" />
          </div>

          <div className="card-flat p-5" data-testid="admin-audit-table">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Filter size={13} className="text-[var(--text-secondary)]" />
                {KIND_OPTIONS.map(k => (
                  <button
                    key={k}
                    onClick={() => setKind(k)}
                    className={`px-2.5 py-1 rounded-md border text-xs transition ${
                      kind === k
                        ? "border-[var(--accent-primary)] text-[var(--accent-primary)] bg-[var(--accent-primary)]/8"
                        : "border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--text-secondary)]"
                    }`}
                    data-testid={`admin-audit-filter-${k}`}
                  >
                    {k === "all" ? "All" : (KIND_LABELS[k]?.label || k)}
                  </button>
                ))}
              </div>
              <div className="text-xs text-[var(--text-muted)]">
                {filtered.length} shown
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-sm text-[var(--text-muted)] py-6">
                No events in this window / filter.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-[var(--text-muted)] border-b border-[var(--border-default)]">
                      <th className="py-2 pr-3 font-medium">When</th>
                      <th className="py-2 pr-3 font-medium">Kind</th>
                      <th className="py-2 pr-3 font-medium">User</th>
                      <th className="py-2 pr-3 font-medium">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r, i) => (
                      <tr key={`${r.at}-${i}`} className="border-b border-[var(--border-default)] last:border-0" data-testid={`admin-audit-row-${i}`}>
                        <td className="py-2 pr-3 text-[11px] text-[var(--text-muted)] whitespace-nowrap">
                          {fmtDateTime(r.at)}
                        </td>
                        <td className="py-2 pr-3">{kindBadge(r.kind)}</td>
                        <td className="py-2 pr-3">
                          <div className="text-[13px] text-[var(--text-primary)]">{r.email || "—"}</div>
                          <div className="text-[10px] font-mono text-[var(--text-muted)]">{r.user_id || "—"}</div>
                        </td>
                        <td className="py-2 pr-3 text-[12px] text-[var(--text-secondary)] max-w-md">
                          {renderDetail(r.kind, r.detail)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <div className="mt-8 flex flex-wrap gap-3 text-xs">
        <Link to="/admin/metrics" className="btn-ghost" data-testid="admin-audit-back-metrics">
          Back to metrics <ArrowRight size={12} />
        </Link>
        <Link to="/admin/users" className="btn-ghost" data-testid="admin-audit-goto-users">
          User lookup <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}

function summarise(items) {
  const out = { admin_extend: 0, trial_started: 0, trial_ended_early: 0 };
  for (const r of items) {
    if (Object.prototype.hasOwnProperty.call(out, r.kind)) out[r.kind] += 1;
  }
  return out;
}

function kindBadge(kind) {
  const cfg = KIND_LABELS[kind] || { label: kind, tone: "muted" };
  const toneCls = {
    warning: "bg-[var(--accent-warning)]/15 text-[var(--accent-warning)]",
    info:    "bg-[var(--accent-primary)]/12 text-[var(--accent-primary)]",
    muted:   "bg-[var(--text-muted)]/15 text-[var(--text-muted)]",
  }[cfg.tone] || "bg-[var(--text-muted)]/15 text-[var(--text-muted)]";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-mono ${toneCls}`}>{cfg.label}</span>;
}

function renderDetail(kind, detail) {
  if (!detail) return "—";
  if (kind === "admin_extend") {
    return (
      <>
        <span className="font-mono">+{detail.days || 0}d</span>
        {detail.reason && <span> · {detail.reason}</span>}
      </>
    );
  }
  if (kind === "trial_started") {
    return <span>started {detail.days || 14}-day trial{detail.source ? ` · ${detail.source}` : ""}</span>;
  }
  if (kind === "trial_ended_early") {
    return <span>ended early · was to expire {fmtDateShort(detail.original_trial_ends_at)}</span>;
  }
  return <span className="font-mono text-[11px]">{JSON.stringify(detail).slice(0, 120)}</span>;
}

function SummaryCard({ label, value, icon, testid }) {
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[var(--text-secondary)]">
        {icon || <Activity size={14} />}
        <div className="label-eyebrow">{label}</div>
      </div>
      <div className="text-2xl font-medium font-mono mt-2">{value}</div>
    </div>
  );
}

function fmtDateShort(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return v; }
}
function fmtDateTime(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return v; }
}
